import FileDownload from "@mui/icons-material/FileDownload";
import { Alert, Button } from "@mui/material";
import Papa from "papaparse";
import { type FC, type MouseEventHandler, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { UUID } from "../../../../utils/uuid";
import { serveCsv } from "../../utils/serve-csv";
import { useMomentPollsContext } from "../context/moment-polls-context";
import { pollContact } from "../utils/poll-contact";

export const PollsExportButton: FC<{ poll_id: UUID }> = ({ poll_id }) => {
  const { error, loading: pollsLoading, data } = useMomentPollsContext();

  const poll = useMemo(() => data?.find((p) => p.poll_id === poll_id), [data, poll_id]);

  const [loading, setLoading] = useState(false);
  const loadData = useCallback(() => {
    setLoading(true);
    try {
      if (!poll) {
        toast.error("Error loading poll data");
        return;
      }

      const preppedData = poll.results.flatMap(({ responses, text }) =>
        responses.map((response) => ({
          contact: pollContact(response),
          when: response.timestamp,
          answer: text,
        })),
      );

      // get file name safe text by removing special characters and truncate to 50 characters
      const shortPollQuestion = poll.question
        .replace(/[^a-zA-Z0-9]/g, "-")
        .toLowerCase()
        .substring(0, 50);

      serveCsv(
        Papa.unparse(preppedData),
        `moment-poll-${shortPollQuestion}-${new Date().toISOString()}.csv`,
      );
    } catch (e) {
      toast.error("Failed to export poll");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [poll]);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      loadData();
    },
    [loadData],
  );

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        Error
      </Alert>
    );
  }

  return (
    <Button
      data-analytics-id="export-moment-poll"
      color="primary"
      loading={loading}
      disabled={!!error || loading || pollsLoading || !poll || !poll.hasResponsed}
      variant="text"
      startIcon={<FileDownload />}
      onClick={handleClick}
    >
      Export
    </Button>
  );
};
